import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { PrismIcon, PrismInput } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
// eslint-disable-next-line import-x/no-named-as-default
import powerCalendarFormatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import { not } from 'ember-truth-helpers';
import { toLocalTimeInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import GeneralDropdownsOtaDropdownClass from 'lumen/components/general/dropdowns/ota-dropdown-class';
import dayjsToDate from 'lumen/helpers/dayjs-to-date';
import { timeout } from 'lumen/utils/waiters';

export default class GeneralFormsFormFieldsDatepickerFormField extends GeneralDropdownsOtaDropdownClass {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked center = null;
  @tracked hasValidDateFormat = true;
  // It saves the date if it has an invalid value and be able to show it in the input. Once the date is corrected its value goes back to null
  @tracked invalidDateInputValue = null;
  @tracked isWithinRange = true;

  // @ts-expect-error: Incorrect type
  dropdownClass = `.datepicker-form-fields-dropdown-${this.elementId}`;
  // @ts-expect-error: Incorrect type
  dropdownClassName = `datepicker-form-fields-dropdown-${this.elementId}`;
  selectDay = true;

  // Manually input date
  setStringDateTask = task({ restartable: true }, async (e) => {
    await timeout();
    // Reset the isWithinRange property to its default value every time a new date is entered
    this.isWithinRange = true;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const dateString = e.target.value;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.hasValidDateFormat = this.dateRegex.test(dateString);
    if (this.hasValidDateFormat) {
      const momentFormat = dayjs.localeData().longDateFormat('L');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const inputDate = dayjs.utc(dateString, momentFormat);
      this.invalidDateInputValue = null;
      this.isWithinRange = this.checkWithinRange(inputDate);
      if (inputDate.isValid() && this.isWithinRange) {
        // @ts-expect-error: Incorrect type
        this.center = toLocalTimeInBrowserTimezone(inputDate, this.timezone);
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.onSelectDate(inputDate);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.invalidDateInputValue = dateString;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.onSelectDate(null);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.invalidDateInputValue = dateString;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.onSelectDate(null);
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get centerWithDefault() {
    if (this.center) {
      return this.center;
    }
    // @ts-expect-error: Incorrect type
    if (this.args.defaultCenter) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return this.hotelAndMarketService.toLocalTime(this.args.defaultCenter);
    }
    if (
      this.convertedDatePickerMinDate?.isAfter(
        this.hotelAndMarketService.getLocalTime().utc(),
      )
    ) {
      return this.convertedDatePickerMinDate;
    }
    return this.hotelAndMarketService.getLocalTime().utc();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedDatePickerMaxDate() {
    // @ts-expect-error: Incorrect type
    if (this.args.datePickerMaxDate) {
      return toLocalTimeInBrowserTimezone(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.args.datePickerMaxDate,
        this.timezone,
      );
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedDatePickerMinDate() {
    // @ts-expect-error: Incorrect type
    if (this.args.datePickerMinDate) {
      return toLocalTimeInBrowserTimezone(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.args.datePickerMinDate,
        this.timezone,
      );
    }
    return null;
  }

  // Since PowerCalendar is not able to work with timezones, we use browser TZ
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedSelectedDate() {
    // @ts-expect-error: Incorrect type
    if (this.args.selectedDate) {
      return toLocalTimeInBrowserTimezone(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.args.selectedDate,
        this.timezone,
      );
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateFormat() {
    return this.dates.partialDateFormat?.YEAR_MONTH_AND_DAY_SHORT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRegex() {
    return this.dates.partialDateFormat?.YEAR_MONTH_AND_DAY_REGEX;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get errorMessage() {
    // @ts-expect-error: Incorrect type
    if (this.args.isInErrorState) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.errorMessage ?? '';
    }

    if (!this.isWithinRange) {
      return this.intl.t('error.date_outside_range');
    }

    return `${this.intl.t('error.enter_valid_date_format')} (${this.formattedDateFormat})`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedConvertedSelectedDate() {
    if (this.invalidDateInputValue) {
      return this.invalidDateInputValue;
    }
    if (!this.convertedSelectedDate && this.isOpen) {
      return ' ';
    }
    if (this.convertedSelectedDate) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return this.convertedSelectedDate.format('L');
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.placeholder;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDateFormat() {
    return this.dateFormat.toLowerCase();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasErrors() {
    return (
      // @ts-expect-error: Incorrect type
      this.args.isInErrorState === true ||
      !this.hasValidDateFormat ||
      !this.isWithinRange
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNextMonths() {
    // @ts-expect-error: Incorrect type
    if (this.args.datePickerMaxDate) {
      return this.centerWithDefault

        .add(1, 'month')
        .startOf('month')
        .isBefore(this.convertedDatePickerMaxDate, 'day');
    }
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPreviousMonths() {
    // @ts-expect-error: Incorrect type
    if (this.args.datePickerMinDate) {
      return this.centerWithDefault

        .subtract(1, 'month')
        .endOf('month')
        .isAfter(this.convertedDatePickerMinDate, 'day');
    }
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasWarnings() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.isInWarningState;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthFormat() {
    return this.dates.partialDateFormat?.YEAR_AND_MONTH_LONG;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get requiredErrorMessage() {
    // @ts-expect-error: Incorrect type
    if (this.args.isInErrorState) {
      return '';
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.requiredErrorMessage ?? this.intl.t('general.required');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timezone() {
    return this.hotelAndMarketService.timezone;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  checkWithinRange(moment) {
    if (this.convertedDatePickerMinDate && this.convertedDatePickerMaxDate) {
      return (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.convertedDatePickerMinDate.isSameOrBefore(moment, 'day') && // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.convertedDatePickerMaxDate.isSameOrAfter(moment, 'day')
      );
    }
    return true;
  }

  @action
  nextMonth(): void {
    if (this.hasNextMonths) {
      // @ts-expect-error: Incorrect type
      this.center = this.centerWithDefault.add(1, 'month');
    }
  }

  @action
  previousMonth(): void {
    if (this.hasPreviousMonths) {
      // @ts-expect-error: Incorrect type
      this.center = this.centerWithDefault.subtract(1, 'month');
    }
  }

  // Selected date from the calendar
  @action
  // @ts-expect-error: Incorrect type
  selectDate(date): void {
    this.invalidDateInputValue = null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.hasValidDateFormat = date.moment.isValid();
    this.isWithinRange = true;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.onSelectDate(date.moment);
    this.toggleDropdown();
  }

  <template>
    {{! @glint-nocheck }}
    <div
      class="date-picker date-picker__form-field flex flex-col
        {{if this.isOpen 'date-picker__form-field--focused'}}"
      ...attributes
    >
      <PrismInput
        @errorMessage={{this.errorMessage}}
        @hasErrors={{this.hasErrors}}
        @hasWarnings={{this.hasWarnings}}
        @helpText={{@helpText}}
        @isTouched={{@isTouched}}
        @label={{@label}}
        @onInput={{perform this.setStringDateTask}}
        @placeholder={{this.formattedDateFormat}}
        @removePadding={{@removePadding}}
        @required={{not @isOptional}}
        @requiredErrorMessage={{this.requiredErrorMessage}}
        @value={{this.formattedConvertedSelectedDate}}
        data-test-field={{@label}}
        {{on "click" this.toggleDropdown}}
      />
    </div>
    {{#if this.isOpen}}
      <div
        class="{{this.dropdownClassName}} rounded-dropdown flex flex-row"
        {{didInsert this.attachOutsideClickHandler}}
      >
        <button
          class="calendar-button {{unless this.hasPreviousMonths 'disabled'}}"
          type="button"
          {{on "click" this.previousMonth}}
        >
          <div class="calendar-button-icon">
            <PrismIcon @color="inherit" @name="chevron-left" />
          </div>
        </button>
        <PowerCalendar
          @center={{this.centerWithDefault}}
          @onSelect={{this.selectDate}}
          @selected={{this.convertedSelectedDate}}
          class="demo-calendar-small"
          as |calendar|
        >
          <calendar.Nav>
            <div class="month-title flex items-center justify-center">
              {{powerCalendarFormatDate
                calendar.center
                this.monthFormat
                locale=calendar.locale
              }}
            </div>
          </calendar.Nav>
          <calendar.Days
            @maxDate={{dayjsToDate this.convertedDatePickerMaxDate}}
            @minDate={{dayjsToDate this.convertedDatePickerMinDate}}
            @showDaysAround={{@showDaysAround}}
            @startOfWeek={{this.dates.startOfWeek}}
          />
        </PowerCalendar>
        <button
          class="calendar-button {{unless this.hasNextMonths 'disabled'}}"
          type="button"
          {{on "click" this.nextMonth}}
        >
          <div class="calendar-button-icon">
            <PrismIcon @color="inherit" @name="chevron-right" />
          </div>
        </button>
      </div>
    {{/if}}
  </template>
}
